import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/carrito.css';
import { useCarrito } from '../context/carritoContext';
import { Trash2 } from 'lucide-react';
import { calculateCartSummary } from '../services/catalogApi';
import ProductosLinea from './ProductosLinea';
import Seo from './Seo';
import { productoPath } from '../utils/slug';

const Carrito = () => {
  const [resumen, setResumen] = useState({ subtotal: 0, envio: 0 });
  const { carrito, eliminarDelCarrito, limpiarCarrito, actualizarCantidad } = useCarrito();
  const navigate = useNavigate();
  const [confirmacion, setConfirmacion] = useState(null);

  const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const tagsMasVendidos = useMemo(() => [6], []);

  useEffect(() => {
    calculateCartSummary(carrito)
      .then((data) => {
        setResumen({
          subtotal: toNumber(data?.subtotal),
          envio: toNumber(data?.envio),
        });
      })
      .catch((err) => console.error(err));
  }, [carrito]);

  useEffect(() => {
    if (!confirmacion) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setConfirmacion(null);
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [confirmacion]);

  const subtotal = toNumber(resumen.subtotal);
  const envio = toNumber(resumen.envio);
  const total = subtotal + envio;

  const handleConfirmarEliminar = () => {
    if (confirmacion?.tipo === 'eliminar' && confirmacion.item) {
      eliminarDelCarrito(confirmacion.item.artId);
      setConfirmacion(null);
    }
  };

  const handleConfirmarVaciar = () => {
    if (confirmacion?.tipo === 'vaciar') {
      limpiarCarrito();
      setConfirmacion(null);
    }
  };

  return (
    <section className="carrito-page">
      <Seo title="Carrito de compras" path="/carrito" noindex />
      <div className="carrito-header">
        <h1>Carrito de compras</h1>
      </div>

      {carrito.length === 0 ? (
        <div className="carrito-empty">
          <h2>Tu carrito esta vacio</h2>
          <p>Explora la tienda y agrega productos para continuar.</p>
          <button className="carrito-btn-comprar" onClick={() => navigate('/tienda')}>Ir a tienda</button>
        </div>
      ) : (
        <div className="carrito-layout">
          <ul className="carrito-lista">
            {carrito.map((item) => {
              const rutaDetalle = item.esCombo ? `/combos/${item.comboId}` : productoPath(item);
              return (
              <li key={item.artId} className="carrito-item">
                <img
                  src={item.artFoto ? `${item.artFoto}` : '/img/default.jpg'}
                  alt={item.artNom}
                  className="carrito-img"
                  onClick={() => navigate(rutaDetalle)}
                />

                <div className="carrito-detalles">
                  <h3 onClick={() => navigate(rutaDetalle)}>{item.artNom}</h3>

                  <div className="carrito-precios">
                    {item.artDescuento > 0 ? (
                      <>
                        <p className="carrito-precio-original">${item.artPrecio.toFixed(2)}</p>
                        <p className="carrito-precio-descuento">${(item.artPrecio - item.artDescuento).toFixed(2)}</p>
                      </>
                    ) : (
                      <p className="carrito-precio-normal">${item.artPrecio.toFixed(2)}</p>
                    )}
                  </div>

                  <div className="carrito-acciones">
                    <div className="cantidad-control">
                      <button
                        className="cantidad-control-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          actualizarCantidad(item.artId, (item.cantidad || 1) - 1);
                        }}
                      >
                        -
                      </button>
                      <span>{item.cantidad || 1}</span>
                      <button
                        className="cantidad-control-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          actualizarCantidad(item.artId, (item.cantidad || 1) + 1);
                        }}
                      >
                        +
                      </button>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmacion({ tipo: 'eliminar', item });
                      }}
                      className="carrito-btn-eliminar"
                      aria-label="Eliminar producto"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </li>
              );
            })}
          </ul>

          <aside className="carrito-resumen">
            <h2>Resumen de compra</h2>

            <div className="desglose">
              <span>Subtotal</span>
              <strong>${subtotal.toFixed(2)}</strong>
            </div>

            <div className="desglose">
              <span>Envio</span>
              <strong>{envio === 0 ? 'Gratis' : `$${envio.toFixed(2)}`}</strong>
            </div>

            <hr className="linea-separadora" />

            <div className="desglose total-final">
              <span>Total</span>
              <strong>${total.toFixed(2)}</strong>
            </div>

            <button onClick={() => navigate('/checkout')} className="carrito-btn-comprar">Continuar</button>
            <button onClick={() => setConfirmacion({ tipo: 'vaciar' })} className="carrito-btn-limpiar">Vaciar carrito</button>
          </aside>
        </div>
      )}

      <section className="carrito-recomendados">
        <ProductosLinea
          tagIds={tagsMasVendidos}
          nombre="Mas vendido"
        />
      </section>

      {confirmacion && (
        <div className="confirm-overlay" onClick={() => setConfirmacion(null)}>
          <div
            className="confirm-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={confirmacion.tipo === 'vaciar' ? 'Confirmar vaciado' : 'Confirmar eliminacion'}
          >
            <div className="confirm-icon">
              <Trash2 size={22} />
            </div>

            {confirmacion.tipo === 'vaciar' ? (
              <>
                <h3>Vaciar el carrito?</h3>
                <p>
                  Se eliminaran <strong>todos los productos</strong> del carrito.
                </p>
              </>
            ) : (
              <>
                <h3>Eliminar este producto?</h3>
                <p>
                  Se quitara <strong>{confirmacion.item.artNom}</strong> de tu carrito.
                </p>
              </>
            )}

            <div className="confirm-acciones">
              <button
                className="confirm-btn-cancelar"
                onClick={() => setConfirmacion(null)}
              >
                Cancelar
              </button>
              <button
                className="confirm-btn-eliminar"
                onClick={confirmacion.tipo === 'vaciar' ? handleConfirmarVaciar : handleConfirmarEliminar}
              >
                {confirmacion.tipo === 'vaciar' ? 'Vaciar' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Carrito;
